import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/shared/Navbar';
import Footer from '../components/shared/Footer';
import { VerificationBadge, AfCFTABadge } from '../components/shared/Badges';
import InquiryModal from '../components/shared/InquiryModal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  MapPin, Package, Truck, Shield, Calculator, MessageSquare,
  ArrowLeft, Share2, Heart, Check, AlertCircle, Loader2, Send
} from 'lucide-react';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [showInquiryModal, setShowInquiryModal] = useState(false);

  const fetchProduct = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      // Fallback to sample product for demo
      setProduct({
        product_id: productId,
        name: 'Premium Cocoa Beans from Ghana',
        description: 'High-quality cocoa beans, ideal for chocolate production. Sourced from sustainable farms in the Ashanti region. Our cocoa beans are fermented and dried using traditional methods to ensure the best flavor profile. Perfect for artisan chocolate makers and large-scale manufacturers alike.',
        category: 'agriculture',
        hs_code: '1801.00',
        price: 3500,
        currency: 'USD',
        moq: 100,
        production_capacity: '500 MT/month',
        country_of_origin: 'Ghana',
        seller_name: 'Ghana Cocoa Board',
        business_id: 'biz_001',
        verification_status: 'verified',
        afcfta_eligible: true,
        certifications: ['Fair Trade', 'Organic', 'Rainforest Alliance'],
        delivery_markets: ['Nigeria', 'Kenya', 'South Africa', 'Egypt', 'Morocco'],
        images: [
          'https://images.unsplash.com/photo-1606312619070-d48b4c652a52?w=800&h=600&fit=crop',
          'https://images.unsplash.com/photo-1481391319762-47dff72954d9?w=800&h=600&fit=crop',
        ],
        created_at: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const handleInquiry = () => {
    if (!isAuthenticated) {
      toast.error('Please login to send an inquiry');
      navigate('/login');
      return;
    }
    setShowInquiryModal(true);
  };

  const handleOrder = () => {
    if (!isAuthenticated) {
      toast.error('Please login to place an order');
      navigate('/login');
      return;
    }
    if (quantity < (product?.moq || 1)) {
      toast.error(`Minimum order quantity is ${product?.moq || 1} units`);
      return;
    }
    toast.success('Order request submitted!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="w-8 h-8 animate-spin text-continental" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container-main py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">Product not found</h1>
          <Button onClick={() => navigate('/marketplace')}>Back to Marketplace</Button>
        </div>
      </div>
    );
  }

  const totalPrice = product.price * quantity;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="container-main py-8">
        {/* Breadcrumb */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-continental mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Marketplace
        </button>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Product Images */}
          <div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100 mb-4">
              <img
                src={product.images?.[0] || 'https://images.unsplash.com/photo-1606312619070-d48b4c652a52?w=800'}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex gap-4">
              {product.images?.slice(0, 4).map((img, i) => (
                <div key={i} className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 cursor-pointer border-2 border-transparent hover:border-continental">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div>
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge variant="secondary" className="capitalize">{product.category}</Badge>
              <VerificationBadge status={product.verification_status} />
              {product.afcfta_eligible && <AfCFTABadge eligible={true} />}
            </div>

            <h1 className="text-3xl font-bold text-charcoal mb-4 font-poppins" data-testid="product-title">
              {product.name}
            </h1>

            <div className="flex items-center gap-4 text-gray-600 mb-6">
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {product.country_of_origin}
              </div>
              <div className="flex items-center gap-1">
                <Shield className="w-4 h-4" />
                {product.seller_name}
              </div>
            </div>

            <p className="text-gray-600 mb-6 leading-relaxed">{product.description}</p>

            {/* Price */}
            <div className="bg-white rounded-xl p-6 border mb-6">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-4xl font-bold text-continental font-poppins">
                  {product.currency} {product.price.toLocaleString()}
                </span>
                <span className="text-gray-500">/ unit</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Package className="w-4 h-4" />
                  MOQ: {product.moq} units
                </div>
                {product.production_capacity && (
                  <div className="flex items-center gap-1">
                    <Truck className="w-4 h-4" />
                    Capacity: {product.production_capacity}
                  </div>
                )}
              </div>
            </div>

            {/* Order Form */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Quantity</label>
                    <Input
                      type="number"
                      min={product.moq}
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(product.moq, parseInt(e.target.value) || 1))}
                      className="w-32"
                      data-testid="quantity-input"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Total Price</label>
                    <p className="text-2xl font-bold text-charcoal">
                      {product.currency} {totalPrice.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button 
                    onClick={handleOrder} 
                    className="flex-1 btn-primary"
                    data-testid="order-btn"
                  >
                    Place Order
                  </Button>
                  <Button 
                    onClick={handleInquiry} 
                    variant="outline" 
                    className="flex-1"
                    data-testid="inquiry-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Send Inquiry
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <div className="flex gap-4">
              <Button variant="ghost" size="sm">
                <Heart className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button variant="ghost" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/tariff-calculator')}
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculate Tariff
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <Tabs defaultValue="details" className="mb-12">
          <TabsList className="mb-6">
            <TabsTrigger value="details">Product Details</TabsTrigger>
            <TabsTrigger value="seller">Seller Information</TabsTrigger>
            <TabsTrigger value="shipping">Shipping & Logistics</TabsTrigger>
            <TabsTrigger value="afcfta">AfCFTA Compliance</TabsTrigger>
          </TabsList>

          <TabsContent value="details">
            <Card>
              <CardContent className="p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-charcoal mb-4">Specifications</h4>
                    <dl className="space-y-3">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">HS Code</dt>
                        <dd className="font-medium">{product.hs_code || 'N/A'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Category</dt>
                        <dd className="font-medium capitalize">{product.category}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Origin</dt>
                        <dd className="font-medium">{product.country_of_origin}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">MOQ</dt>
                        <dd className="font-medium">{product.moq} units</dd>
                      </div>
                    </dl>
                  </div>
                  <div>
                    <h4 className="font-semibold text-charcoal mb-4">Certifications</h4>
                    <div className="flex flex-wrap gap-2">
                      {product.certifications?.map((cert, i) => (
                        <Badge key={i} variant="secondary" className="gap-1">
                          <Check className="w-3 h-3" />
                          {cert}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="seller">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 bg-continental/10 rounded-xl flex items-center justify-center">
                    <Shield className="w-8 h-8 text-continental" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-charcoal">{product.seller_name}</h3>
                    <p className="text-gray-500">{product.country_of_origin}</p>
                    <VerificationBadge status={product.verification_status} size="small" />
                  </div>
                </div>
                <Button variant="outline" onClick={handleInquiry}>
                  Contact Seller
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="shipping">
            <Card>
              <CardContent className="p-6">
                <h4 className="font-semibold text-charcoal mb-4">Available Delivery Markets</h4>
                <div className="flex flex-wrap gap-2 mb-6">
                  {product.delivery_markets?.map((market, i) => (
                    <Badge key={i} variant="outline">{market}</Badge>
                  ))}
                </div>
                <Button onClick={() => navigate('/logistics')}>
                  <Truck className="w-4 h-4 mr-2" />
                  Get Shipping Quotes
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="afcfta">
            <Card>
              <CardContent className="p-6">
                {product.afcfta_eligible ? (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <Check className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-charcoal mb-2">AfCFTA Eligible Product</h4>
                      <p className="text-gray-600 mb-4">
                        This product qualifies for preferential tariff treatment under the African Continental 
                        Free Trade Area agreement. Up to 90% tariff reduction may apply.
                      </p>
                      <Button onClick={() => navigate('/tariff-calculator')}>
                        <Calculator className="w-4 h-4 mr-2" />
                        Calculate Exact Tariff
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-charcoal mb-2">Eligibility Check Required</h4>
                      <p className="text-gray-600 mb-4">
                        Use our tariff calculator to determine if this product qualifies for 
                        AfCFTA preferential rates based on your specific trade route.
                      </p>
                      <Button onClick={() => navigate('/tariff-calculator')}>
                        Check Eligibility
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Footer />

      {/* Inquiry Modal */}
      <InquiryModal
        open={showInquiryModal}
        onClose={() => setShowInquiryModal(false)}
        product={product}
        seller={{ user_id: product?.seller_id, name: product?.seller_name }}
      />
    </div>
  );
};

export default ProductDetailPage;
